import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { BehaviorSubject, Subject, combineLatest, of } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';

import {
  DeviceLocationsElement,
  DeviceLocationsSite,
  SynapseService,
} from '../../../core/services/synapse';
import { ToggleComponent } from '../../../shared/components/toggle/toggle.component';
import { Permission } from '../../../shared/constants/permissions';
import { SharedModule } from '../../../shared/shared.module';
import { canUseSampleData } from '../../../shared/utils/sample-data-toggle.util';
import * as fromStore from '../../../store';
import { MaplibreMapComponent } from '../../components/maplibre-map/maplibre-map.component';
import { WidgetObservationsComponent } from '../../components/widgets/widget-observations/widget-observations.component';
import { WidgetOrdersComponent } from '../../components/widgets/widget-orders/widget-orders.component';
import { WidgetServicesComponent } from '../../components/widgets/widget-services/widget-services.component';
import { WidgetSummaryComponent } from '../../components/widgets/widget-summary/widget-summary.component';
import { WidgetTicketsComponent } from '../../components/widgets/widget-tickets/widget-tickets.component';
import { SERVICE_STATUS_BY_SEVERITY } from '../../lib/constants';
import { getHighestStatus, getStatusColor } from '../../lib/functions';

import {
  Category,
  MapMarker,
  MarkerElement,
  ServiceStatus,
} from '../../lib/types';

@Component({
  selector: 'app-overall-network-status-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatProgressBarModule,
    NgxPermissionsModule,
    SharedModule,
    ToggleComponent,
    MaplibreMapComponent,
    WidgetSummaryComponent,
    WidgetTicketsComponent,
    WidgetOrdersComponent,
    WidgetServicesComponent,
    WidgetObservationsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './overall-network-status-page.component.html',
})
export class OverallNetworkStatusPageComponent implements OnInit, OnDestroy {
  private readonly synapseService = inject(SynapseService);

  private readonly store = inject(Store<fromStore.State>);

  private readonly actions = inject(Actions);

  private readonly permissionsService = inject(NgxPermissionsService);

  private readonly destroy$ = new Subject<void>();

  private readonly currentUser$ = this.store
    .select(fromStore.getCurrentUser)
    .pipe(catchError(() => of(null)));

  readonly Permission = Permission;

  readonly getStatusColor = getStatusColor;

  readonly showMockData$ = new BehaviorSubject<boolean>(false);

  readonly canShowSampleDataToggle = signal<boolean>(false);

  readonly iqObservationsLoadings = signal<boolean>(false);

  readonly categories = signal<Category[]>(
    [...SERVICE_STATUS_BY_SEVERITY]
      .reverse()
      .map((s) => ({ label: s, count: 0 })),
  );

  readonly markers = signal<MapMarker[]>([]);

  readonly loading = signal<boolean>(false);

  readonly observations = toSignal(
    this.actions.pipe(
      ofType(fromStore.GET_IQ_OBSERVATIONS_SUCCESS),
      map((action: fromStore.GetIQObservationsSuccess) => {
        this.iqObservationsLoadings.set(false);
        return action.payload;
      }),
    ),
  );

  async ngOnInit(): Promise<void> {
    this.initializeSampleDataToggleVisibility();

    const { canReadBilling } = await this.getPageAccess();

    this.initializeIqData();

    if (canReadBilling) {
      this.initializeBillingData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetState(): void {
    this.loading.set(true);
    this.categories.set(
      [...SERVICE_STATUS_BY_SEVERITY]
        .reverse()
        .map((s) => ({ label: s, count: 0 })),
    );
    this.markers.set([]);
  }

  private async getPageAccess(): Promise<{
    canReadBilling: boolean;
  }> {
    const canReadBilling = await this.permissionsService.hasPermission(
      Permission.BILLING_READ,
    );

    return { canReadBilling };
  }

  private initializeIqData(): void {
    combineLatest([this.showMockData$, this.currentUser$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([showMockData, user]) => {
        const accountName = user?.Account?.Name;
        const canViewIQ = user?.Permissions?.includes(
          Permission.NITEL_IQ_PARTICIPANT,
        );
        if (!accountName || !canViewIQ) return;
        this.loadIQData({ showMockData, accountName });
      });

    combineLatest([this.showMockData$, this.currentUser$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([showMockData, user]) => {
        const canViewIQ = user?.Permissions?.includes(
          Permission.NITEL_IQ_PARTICIPANT,
        );
        if (!canViewIQ) {
          this.iqObservationsLoadings.set(false);
          return;
        }

        this.iqObservationsLoadings.set(true);
        this.store.dispatch(new fromStore.GetIQObservations({ showMockData }));
      });
  }

  private initializeSampleDataToggleVisibility(): void {
    this.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) =>
        this.canShowSampleDataToggle.set(canUseSampleData(user)),
      );
  }

  private initializeBillingData(): void {
    this.store
      .select(fromStore.getPaymentsLoaded)
      .pipe(take(1))
      .subscribe((loaded) => {
        if (!loaded) {
          this.store.dispatch(new fromStore.LoadPayments());
        }
      });

    this.store
      .select(fromStore.getInvoicesLoaded)
      .pipe(take(1))
      .subscribe((loaded) => {
        if (!loaded) {
          this.store.dispatch(new fromStore.LoadInvoices());
        }
      });
  }

  private loadIQData(args: {
    showMockData: boolean;
    accountName: string;
  }): void {
    this.resetState();
    const params = {
      showMockData: args.showMockData,
      accountName: args.accountName,
      page: 1,
    };

    this.synapseService
      .getDeviceLocations(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.categories.set(this.getCategories(data));
          this.markers.set(this.getMarkers(data));
          this.loading.set(false);
        },
        error: (err) => {
          // eslint-disable-next-line no-console
          console.error('Error fetching device locations:', err);
          this.loading.set(false);
        },
      });
  }

  private getMarkers(args: {
    elements: DeviceLocationsElement[];
    sites: DeviceLocationsSite[];
  }): MapMarker[] {
    if (!args?.elements || !Array.isArray(args.elements)) {
      return [];
    }
    const elementsByServiceLookup = Object.fromEntries<MarkerElement[]>(
      args.elements.map((el) => [
        el.Name,
        el.Elements__r?.records?.map((element) => ({
          name: element.Name,
          recordType: {
            name: element.RecordType?.Name,
          },
          vendor: {
            circuitId: element.Vendor_Circuit_Id__c,
            name: element.Vendor__r?.Name,
          },
        })) ?? [],
      ]),
    );

    const markersLookup: Record<string, MapMarker> = {};
    args.sites.forEach((site) => {
      const key = `${site.latitude}:${site.longitude}`;

      const mapMarker: MapMarker = markersLookup[key] ?? {
        getStatus: () => getHighestStatus(mapMarker.services),
        longitude: site.longitude,
        latitude: site.latitude,
        services: [],
      };

      mapMarker.services.push({
        elements: elementsByServiceLookup[site.name] ?? [],
        status: site.status as ServiceStatus,
        recordType: site.record_type,
        source: site.source,
        name: site.name,
      });

      markersLookup[key] = mapMarker;
    });

    return Object.values(markersLookup);
  }

  private getCategories(args: {
    sites: DeviceLocationsSite[] | undefined;
  }): Category[] {
    const sites = Array.isArray(args?.sites) ? args.sites : [];
    const statusCountMap = sites.reduce(
      (acc, site) => {
        acc[site.status] = (acc[site.status] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    return [...SERVICE_STATUS_BY_SEVERITY].reverse().map((status) => ({
      label: status,
      count: statusCountMap[status] || 0,
    }));
  }
}
